import { blue, common, grey } from '@mui/material/colors';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import 'moment/locale/es'; // Import Spanish locale
import { GoogleOAuthProvider } from '@react-oauth/google';
import ThemeProvider from 'client/util/ThemeProvider';
import useRegisterNavigate from 'client/util/useRegisterNavigate';
import Confirm from 'components/Confirm/Confirm';
import React, { useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { setLocale } from '../../AppActions';
import favicon from '../../favicon.png';
import muiTheme from '../../muiTheme';
import { loginError } from '../Auth/AuthActions';
import styles from './App.css';

const _blue100 = blue['100'];
const _blue500 = blue['500'];
const _blue700 = blue['700'];
const _darkBlack = grey[900];
const _fullBlack = common.black;
const _grey100 = grey['100'];
const _grey300 = grey['300'];
const _grey400 = grey['400'];
const _grey500 = grey['500'];
const { _white } = common;

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const previousPathname = useRef(location.pathname);

  // Register navigate function with global navigation service
  useRegisterNavigate();

  useEffect(() => {
    const localeMatch = location.pathname.match(/^\/(e[sn])($|\/)/);
    if (localeMatch) {
      dispatch(setLocale(localeMatch[1]));
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    // If pathname changed, clear errors
    if (previousPathname.current !== location.pathname) {
      dispatch(loginError(null));
    }
    previousPathname.current = location.pathname;
  }, [location.pathname, dispatch]);

  const { locale } = useIntl();

  // Set global moment locale when app locale changes
  useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        <title>Promoter Ninja</title>
        <link rel="shortcut icon" type="image/png" href={favicon} />
        <body className={styles.body} />
      </Helmet>
      <GoogleOAuthProvider clientId={process.env.GOOGLE_OAUTH_CLIENT_ID}>
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={muiTheme}>
            <ThemeProvider theme={muiTheme}>
              <LocalizationProvider adapterLocale={locale} dateAdapter={AdapterMoment}>
                <Outlet />
                <Confirm />
              </LocalizationProvider>
            </ThemeProvider>
          </MuiThemeProvider>
        </StyledEngineProvider>
      </GoogleOAuthProvider>
    </>
  );
}

export default App;
